import { oneBtnDialog, oneBtnHtmlDialog, jwtDecoder, logging } from '@/utils/utils';
import { localStorageEnums } from '@/enums/localStorage.enums';
import chinaInapi from '@/apis/chinaInapi';
import { resolveLandingPath } from '@/utils/landing';
// @MX:NOTE: [AUTO] SPEC-CHINA-INAPI-COMPANIES-PRIVILEGES-001 B2 — /companies 호출 chinaInapi로 전환

// admin-manufacturer/src/mixins/signInMixin.js 패턴 이식
// 변경점:
// 1. admin_dentiumchina routing은 'FollowerList' 유지 (admin-china-manufacturer 구버전 보존)
// 2. getUserInfo() try/catch 보강 — DENTAL_LABORATORY 같은 후속 호출 실패 케이스에서
//    "로그인 실패" dialog 오인 + router.push 미실행 동시 방지
export const signInMixin = {
  methods: {
    /**
     * 로그인 API 호출 (roles 배열 순회)
     * @param {string} email - 사용자 이메일
     * @param {string} password - 사용자 비밀번호
     * @param {Array<string>} roles - 시도할 role 배열
     * @returns {Promise<{response: Object|null, lastError: Error|null}>}
     */
    async performLogin(email, password, roles) {
      let loginResponse = null;
      let lastError = null;

      for (const role of roles) {
        try {
          // @MX:NOTE: [AUTO] SPEC-CHINA-INAPI-AUTH-002 B2 — /auth/login 호출 chinaInapi로 전환
          loginResponse = await chinaInapi.post(`/auth/login`, {
            username: email,
            password: password,
            role: role,
          });
          break; // 성공하면 루프 종료
        } catch (error) {
          lastError = error;
          // 다음 role로 계속 시도
        }
      }

      return { response: loginResponse, lastError };
    },

    /**
     * 로그인 성공 처리
     * @param {Object} response - API 응답 객체
     * @param {string} email - 사용자 이메일
     */
    async handleLoginSuccess(response, email) {
      await oneBtnDialog(
        this.$t('signin_login_done_title'),
        this.$t('signin_login_done_content'),
        false
      );

      localStorage.setItem('accessTokenManu', response.data.accessToken);
      localStorage.setItem('refreshTokenManu', response.data.refreshToken);

      // F1: 신규 생성/비밀번호 초기화 계정만 강제 변경 게이트.
      // passwordChangeRequired만 사용(신규 생성/초기화 시 BE가 true로 설정).
      // isFirstLogin='Y'는 사용 안 함 — 마스터 등 레거시 계정이 isFirstLogin='Y'/passwordChangeRequired=false
      // 상태일 수 있어, 운영자가 갑자기 변경 강제당하는 것을 방지.
      const needsPwChange = response.data.passwordChangeRequired === true;
      if (needsPwChange) {
        localStorage.setItem('pwChangeRequiredManu', 'Y');
        // 로그인마다 skip flag 제거 → '다음에 변경하기'로 미뤘어도 비번 변경 전까지 매 로그인 폼 재강제
        sessionStorage.removeItem('pwChangeSkippedManu');
        // [분열 방지] 이 early-return이 아래 applyLocale('login')까지 건너뛰면, 로그아웃 RESET으로
        // 갈라진 locale이 이 세션 내내 복구되지 않는다(2026-08-13 실관측). 라우팅 전에 적용한다.
        try {
          await this.$store.dispatch('locale/applyLocale', {
            source: 'login',
            payload: {
              account: response.data?.account || null,
              company: response.data?.company || null,
            },
          });
        } catch (e) {
          console.warn('applyLocale failed on login (pw-change path):', e);
        }
        this.$router.push('/force-password-change');
        return;
      } else {
        localStorage.removeItem('pwChangeRequiredManu');
        sessionStorage.removeItem('pwChangeSkippedManu');
      }

      // REQ-LOCALE-FE-002/004: login 응답의 account.locale + company.locale로 applyLocale 즉시 실행
      // account.locale > company.locale > 'zh' 우선순위는 Vuex action 내부에서 처리
      try {
        await this.$store.dispatch('locale/applyLocale', {
          source: 'login',
          payload: {
            account: response.data?.account || null,
            company: response.data?.company || null,
          },
        });
      } catch (e) {
        console.warn('applyLocale failed on login:', e);
      }

      // getUserInfo는 부수적 정보 조회 — 실패해도 로그인 자체는 성공으로 처리
      // (DENTAL_LABORATORY 등 type mismatch로 401 떨어져도 router.push 보장)
      try {
        await this.getUserInfo();
      } catch (e) {
        console.warn('getUserInfo failed but login token is valid:', e);
      }

      window.checkIsLoggedIn({
        isLoggedIn: true,
        accessToken: localStorage.getItem('accessTokenManu'),
        refreshToken: localStorage.getItem('refreshTokenManu'),
      });

      // 보유 privileges의 첫 메뉴로 진입 (resolveLandingPath: 없으면 /no-menu — CustomizeMenu fallback 제거)
      let priv = null;
      try {
        priv = await this.$store.dispatch('getPrivileges', { username: email, force: true });
      } catch (e) {
        console.warn('getPrivileges failed at login routing:', e);
      }
      this.$router.push(resolveLandingPath(priv));
    },

    /**
     * 로그인 실패 처리 (에러 타입별 분기)
     * @param {Error} lastError - 마지막 에러 객체
     */
    async handleLoginError(lastError) {
      if (!lastError || !lastError.response) {
        // 네트워크 에러 또는 서버 응답 없음
        await oneBtnDialog(
          this.$t('signin_login_err_title'),
          this.$t('signin_login_err_content'),
          false
        );
        return;
      }

      const status = lastError.response.status;

      if (status === 400) {
        await this.handle400Error(lastError);
      } else if (status === 401) {
        await this.handle401Error(lastError);
      } else if (status === 403) {
        // F5: 계정 잠금 (5회 오류 초과) — "Account locked. Try again in N minute(s)."
        await this.handle403LockoutError(lastError);
      } else {
        await oneBtnDialog(
          this.$t('signin_login_err_title'),
          this.$t('signin_login_err_content'),
          false
        );
      }
    },

    /**
     * 400 에러 처리 (잘못된 계정 타입 — 병원 계정으로 manufacturer 시도 등)
     * @param {Error} error - 에러 객체
     */
    async handle400Error(error) {
      logging().exceptProd('env==>', process.env.NODE_ENV);

      if (error.response.data.message === this.$t('signin_wrong_type_err_content')) {
        let href;
        if (process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'local') {
          href = 'https://intest-hospital.certi.life/signin';
        } else if (process.env.NODE_ENV === 'stage') {
          href = 'https://stg-hospital.certi.life/signin';
        } else if (process.env.NODE_ENV === 'production') {
          href = 'https://hospital.certi.life/signin';
        }
        await oneBtnHtmlDialog(
          '로그인 실패',
          `<span>병원 계정입니다.<br><span>아래 링크에서 로그인 해주세요.</span><br><br><a href="${href}" class="forwardLink">🔗 병원 페이지에서 로그인하기</a></span>`,
          false
        );
      } else {
        await oneBtnDialog(
          this.$t('signin_wrong_access_err_title'),
          this.$t('signin_wrong_access_err_content'),
          false
        );
      }
    },

    /**
     * F5: 403 계정 잠금 에러 처리
     * BE 응답: { message: "Account locked. Try again in N minute(s).", statusCode: 403 }
     * @param {Error} error - 에러 객체
     */
    async handle403LockoutError(error) {
      const data = error?.response?.data || {};
      // [2026-08-13 중앙화] BE가 구조화 필드(errorCode/remainingMinutes/policy)를 내려준다 —
      // 이를 우선 사용하고, 구버전 BE 대비 영어 메시지 정규식 파싱은 fallback으로 유지.
      const rawMessage = data.message || '';
      const minuteMatch = rawMessage.match(/(\d+)\s+minute/i);
      const minutes = data.remainingMinutes ?? (minuteMatch ? Number(minuteMatch[1]) : null);
      let localizedMsg = minutes != null
        ? this.$t('signin_account_locked_minutes', { minutes })
        : this.$t('signin_account_locked');
      // 정책 안내 병기 — Swal text는 줄바꿈이 접히므로 한 줄 요약 키를 쓴다
      localizedMsg = `${localizedMsg} ${this.$t('acc_lock_policy_short')}`;
      await oneBtnDialog(this.$t('signin_login_err_title'), localizedMsg, false);
    },

    /**
     * 401 에러 처리 (인증 실패)
     * 각 컴포넌트에서 오버라이드 가능
     * @param {Error} error - 에러 객체
     */
    async handle401Error(error) {
      console.log(error);

      let message = error.response.data.message;
      if (message == '아이디 또는 비밀번호를 확인해주세요.') {
        message = this.$t('signin_login_err_content');
      }

      await oneBtnDialog(this.$t('signin_login_err_title'), message, false);
    },

    /**
     * 사용자 정보 조회 (회사 정보 + store 동기화)
     * 호출자(handleLoginSuccess)가 try/catch로 감싸므로 throw 가능
     */
    async getUserInfo() {
      const companyId = jwtDecoder(localStorage.getItem('accessTokenManu'));
      const response = await chinaInapi.get(`/companies/${companyId}`);
      if (response.status === 200 || response.status === 201) {
        const { data } = response;
        localStorage.setItem(localStorageEnums.CompanyInfo, JSON.stringify(data));
        if (localStorage.getItem('accessTokenManu')) {
          this.$store.dispatch('getCompanyInfo', this);
        }
      }
    },
  },
};
